import type { MouseEvent } from 'react';

function skipToContent(event: MouseEvent<HTMLAnchorElement>) {
  if (event.defaultPrevented || event.button !== 0 ||
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  // Portal pages already own their main landmark. Marketing pages without one
  // can focus their semantic page heading without introducing a nested main.
  const target = document.getElementById('main-content') ??
    document.querySelector<HTMLElement>('main, [role="main"]') ??
    document.querySelector<HTMLElement>('h1');
  if (!target) {
    // A prior page may have supplied its own id. Loading content must not
    // follow that stale fragment before the current page has a focus target.
    event.preventDefault();
    event.currentTarget.setAttribute('href', '#main-content');
    return;
  }

  event.preventDefault();
  if (!target.id) target.id = 'main-content';
  if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
  const hash = `#${encodeURIComponent(target.id)}`;
  event.currentTarget.setAttribute('href', hash);
  window.history.replaceState(window.history.state, '', hash);

  // Keep content below the fixed 4rem navigation. Restore page-owned styling
  // immediately after the synchronous scroll and leave router history intact.
  const margin = target.style.scrollMarginTop;
  target.style.scrollMarginTop = '5rem';
  target.scrollIntoView({ behavior: 'instant', block: 'start' });
  target.style.scrollMarginTop = margin;
  target.focus({ preventScroll: true });
}

export function SkipToMainContent() {
  return (
    <a
      href="#main-content"
      onClick={skipToContent}
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 bg-background text-foreground px-4 py-2 rounded shadow"
    >
      Skip to main content
    </a>
  );
}
